import some from "lodash/some";
import pluralize from "pluralize";
import {useContext, useEffect, useState} from "react";
import {TbPlayerPause, TbPlayerPlay} from "react-icons/tb";

import {ButtonWithMutationAndConfirmationDialog} from "@/components/ButtonWithMutationAndConfirmationDialog/ButtonWithMutationAndConfirmationDialog.tsx";
import {ChecksContext} from "@/components/context/ChecksContext.tsx";
import {RadioGroup} from "@/components/form/RadioGroup.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {GQL_CHECKS_QUERY2, GQL_UPDATE_CHECK_IS_PAUSED} from "@/lib/queries/checks.ts";

import {CheckActionType} from "./CheckActionType.tsx";

export interface BulkPauseResumeActionButtonWithConfirmationProps {
  selectedItemsIds: number[];
  actionType: CheckActionType;
  onSuccess?: () => void;
}

export const BulkPauseResumeActionButtonWithConfirmation = ({
  selectedItemsIds,
  actionType,
  onSuccess,
}: BulkPauseResumeActionButtonWithConfirmationProps) => {
  const checks = useContext(ChecksContext);
  const selectedChecks = selectedItemsIds.map((id) => checks[id]);
  const hasDownChecksToPause = some(selectedChecks, (ch) => !ch.is_paused && !ch.cached_state_is_up);
  const [sendResolvedNotifications, setSendResolvedNotifications] = useState(hasDownChecksToPause ? "true" : "false");
  useEffect(() => {
    setSendResolvedNotifications(hasDownChecksToPause ? "true" : "false");
  }, [hasDownChecksToPause, selectedItemsIds]);
  let title = "Confirm";
  if (actionType == CheckActionType.PAUSE) {
    if (hasDownChecksToPause) {
      title = "Confirm Bulk Pause and Resolve Notification?";
    } else {
      title = "Confirm Bulk Pause?";
    }
  }
  return (
    <ButtonWithMutationAndConfirmationDialog
      dialogTitle={title}
      tooltip={`${actionType} Checks`}
      dialogSize={actionType == CheckActionType.PAUSE ? "lg" : undefined}
      variant={"tableCrown"}
      dialogElement={
        actionType == CheckActionType.RESUME ? (
          <p>
            Are you sure you want to <b>Resume</b> {pluralize("this", selectedItemsIds.length)}{" "}
            {selectedItemsIds.length} Checks?
          </p>
        ) : (
          <div className="px-lg-4 py-lg-4">
            <p>Pausing the Checks will stop monitoring and no further updates from the Checks will be sent.</p>
            {hasDownChecksToPause && (
              <>
                <p>Would you like to send a resolve notification for the Ongoing Alerts?</p>
                <div className="px-2">
                  <RadioGroup
                    name="send_resolved_notifications"
                    value={sendResolvedNotifications}
                    options={[
                      {value: "true", label: "Yes, send a resolve notification"},
                      {value: "false", label: "No, do not send a resolve notification"},
                    ]}
                    onChange={(v) => setSendResolvedNotifications(v)}
                  />
                </div>
              </>
            )}
          </div>
        )
      }
      mutation_variables={{
        ids: selectedItemsIds,
        is_paused: actionType === CheckActionType.PAUSE,
        send_resolved_notifications: sendResolvedNotifications === "true",
      }}
      mutation_hook_options={{
        refetchQueries: [GQL_CHECKS_QUERY2],
      }}
      mutation_GQL={GQL_UPDATE_CHECK_IS_PAUSED}
      postMutationAction={onSuccess}
    >
      {actionType === CheckActionType.RESUME && (
        <>
          <Icon icon={TbPlayerPlay} />
          <span>Resume</span>
        </>
      )}
      {actionType === CheckActionType.PAUSE && (
        <>
          <Icon icon={TbPlayerPause} />
          <span>Pause</span>
        </>
      )}
    </ButtonWithMutationAndConfirmationDialog>
  );
};
